import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { packagesAPI, track } from '../../services/api';
import './PackageBar.css';

/**
 * "Which exam am I looking at?" — the one control, in every surface.
 *
 * This lived inside PackageBar, which meant it existed only INSIDE a package.
 * From the ESLCE dashboard the way to another exam was a promo card; from a
 * package it was this dropdown. One intention, two affordances, in two places
 * — which is what made moving between exams feel improvised rather than
 * designed.
 *
 * Pulling it out lets the dashboard mount the identical control, so the answer
 * to "where do I switch?" is the same wherever a student is standing. ESLCE is
 * listed alongside the packages because it IS one — a free program in the same
 * catalog — and treating it as the app with packages bolted on is the root of
 * the asymmetry.
 *
 * `current` is the catalog key of wherever the student is now: a package key
 * inside a package, `esslce` on the dashboard.
 */

type Held = { key: string; name: string; home: string; current: boolean };

const PackageSwitcher: React.FC<{ current: string; currentName: string }> = ({
  current,
  currentName,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [held, setHeld] = useState<Held[] | null>(null);
  const wrap = useRef<HTMLDivElement>(null);

  // Close on any outside click or Escape — a menu that traps you is worse
  // than no menu.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Fetched on first open, not on mount: most visits never touch the switcher,
  // and a page a student came to read should not spend a request proving they
  // only hold one package.
  const openMenu = async () => {
    setOpen((v) => !v);
    if (held) return;
    try {
      const { packages } = await packagesAPI.list();
      setHeld(
        (packages || [])
          .filter((p) => p.access)
          .map((p) => ({
            key: p.key,
            name: p.name,
            home: p.home || '/dashboard',
            current: p.key === current,
          }))
      );
    } catch {
      setHeld([]);
    }
  };

  const go = (to: string, how: string) => {
    track('package.switcher.navigate', { from: current, to: how });
    setOpen(false);
    navigate(to);
  };

  return (
    <div className="pb-switch" ref={wrap}>
      <button
        className="pb-current"
        onClick={openMenu}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className="pb-pkgname">{currentName}</span>
        <span className="pb-caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="pb-menu" role="menu">
          <div className="pb-menuhead">What you're studying</div>
          {held === null && <div className="pb-menuempty">Loading…</div>}
          {held?.length === 0 && <div className="pb-menuempty">Nothing else yet.</div>}
          {held?.map((h) => (
            <button
              key={h.key}
              className={`pb-menuitem${h.current ? ' on' : ''}`}
              role="menuitem"
              onClick={() => go(h.home, h.key)}
            >
              <span>{h.name}</span>
              {h.current && <span className="pb-tick" aria-label="current">✓</span>}
            </button>
          ))}
          <div className="pb-menusep" />
          <button
            className="pb-menuitem"
            role="menuitem"
            onClick={() => go('/packages', 'catalog')}
          >
            Browse all packages
          </button>
        </div>
      )}
    </div>
  );
};

export default PackageSwitcher;
